// api/Engine/aiConversion.ts
import { AxiosInstance, AxiosResponse } from "axios"
import { AIConversionResponse } from "types/AIConversion.types"

/**
 * Converts a DOCX questionnaire file to DATA(Q) format using AI
 *
 * @param axiosInstance - Authenticated axios instance
 * @param file - The DOCX file to convert
 * @param projectId - The project ID for the conversion
 * @param isAsync - Indicates whether the conversion should be run asynchronously
 * @returns Promise containing the conversion response
 * @throws Will throw an error if the conversion fails
 */
export async function convertQuestionnaireWithAI(
  axiosInstance: AxiosInstance,
  file: File,
  projectId: string,
  isAsync: boolean
): Promise<AxiosResponse<AIConversionResponse | string>> {
  // Create FormData for file upload
  const formData = new FormData()
  formData.append("document", file)
  formData.append("projectId", projectId)

  // Configure request for file upload
  const config: {
    headers: {
      "Content-Type": string
      "ASync-Request"?: number
    }
  } = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }

  // Conditionally add the ASync-Request header if isAsync is true
  if (isAsync) {
    config.headers["ASync-Request"] = 10
  }

  const response = await axiosInstance.post<AIConversionResponse | string>(
    "/convert-questionnaire",
    formData,
    config
  )

  return response
}

/**
 * Checks the health status of the AI conversion server
 *
 * @param axiosInstance - Authenticated axios instance
 * @returns Promise containing the health check response
 */
export async function checkAIConversionHealth(
  axiosInstance: AxiosInstance
): Promise<
  AxiosResponse<{
    status: string
    timestamp: string
    documentationLoaded: boolean
    geminiConfigured: boolean
    apiConfigured: boolean
    version: string
  }>
> {
  const response = await axiosInstance.get("/health")
  return response
}
